import type { ParsedRecord } from "@/features/patient/records/api/dataPipelineApi";
import type { VisitEntry } from "@/features/patient/dashboard/components/RecentVisitsModal";

// Only a real "YYYY-MM-DD" day: a free-text date ("03/14/2025", "March 2024")
// crashed the dashboard's visit strip, which does date maths on it.
function toDateOnly(value: unknown): string {
  if (typeof value !== "string") return "";
  const day = value.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(day) && !Number.isNaN(Date.parse(`${day}T00:00:00Z`)) ? day : "";
}

function initialsFromName(name: string): string {
  const initials = name
    .split(/[\s,]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();
  return initials || "?";
}

/** Raw (sortable) visit date, kept separate from any display formatting. */
export function visitSortDate(record: ParsedRecord): string {
  return toDateOnly(record.fields.start_date);
}

export function adaptVisit(record: ParsedRecord): VisitEntry {
  const f = record.fields;
  // practitioner_name is resolved server-side (queries.py) by joining the
  // encounter's practitioner_ref back to the Practitioner record's real name —
  // only ever populated for a structured Epic/Athena/C-CDA export, which has
  // a real FHIR reference to join on. A PDF/image upload has no such
  // reference (nothing links an LLM-extracted Encounter to an LLM-extracted
  // Practitioner row), so for that path the doctor's name is instead
  // captured directly on the encounter itself as free text — see
  // practitioner_display (ai_extraction/schema.py's EncounterExtraction).
  // Falls back further to the facility/department name when neither is
  // available (e.g. re-uploaded before the join existed, or the source
  // never named a provider for that visit at all).
  const doctor =
    (f.practitioner_name as string) ||
    (f.practitioner_display as string) ||
    (f.service_provider as string) ||
    (f.location_display as string) ||
    "Unknown provider";
  // The practitioner's real specialty/credential (queries.py resolves this from the
  // same Practitioner row as practitioner_name). Only meaningful when the real
  // practitioner was actually resolved — a facility/department name (the `doctor`
  // fallback above) has no "qualification" to show.
  const specialty = f.practitioner_name ? (f.practitioner_qualification as string) || "" : "";

  return {
    date: toDateOnly(f.start_date) || new Date(0).toISOString().slice(0, 10),
    doctor,
    specialty,
    initials: initialsFromName(doctor),
  };
}
